<template>
  <div>
    <p v-if="activeLocation !='automatedCoachingManualMessages' && activeLocation!='heraMessengerNewMessages' && activeLocation!='associateListCustomMessages' && activeLocation!='automatedRecurringMessages'" class="text-sm font-semibold pb-2">Send this message to all actively rostered Associates via...</p>
    <el-radio-group v-model="selectedChannel" :class="activeLocation === 'heraMessengerNewMessages' ? 'radio-group-horizontal' : 'radio-group'">
      <el-radio label="SMS" :disabled="isSmsDisabled">
        <p class="wrap-text"><b>SMS</b> {{ smsLabel }}</p>
      </el-radio>
      <div
        v-if="
          (selectedChannel === 'SMS' || isSmsDisabled) &&
          staffSmsOnlyList.length
        "
        class="text-red-600 text-xs pl-6 pb-4"
      >
        <div class="font-semibold">
          The following selected {{ $t("label.associates") }} will not be sent
          this SMS because they have opted out of SMS to their personal phone:
        </div>
        <ul class="disc-list">
          <li
            v-for="(staff, index) in staffSmsOnlyList"
            :key="`sms-${index}`"
          >
            {{ staff }}
          </li>
        </ul>
      </div>
      <template v-if="allowDeviceCheckbox && showDeviceCheckbox">
        <el-radio
        label="Device"
        :disabled="isDeviceDisabled"
      >
        <p class="wrap-text"><b>SMS</b> {{ deviceLabel }}</p>
      </el-radio>
      <div
        v-if="selectedChannel === 'Device'"
        class="text-red-600 text-xs pl-6 pb-4"
      >
        <div class="font-semibold">
          <b>Warning:</b>
          By sending messages to your shared company devices, you understand that anyone with the device will be able to read past messages.
        </div>
      </div>
      <div
        v-if="
          selectedChannel === 'Device' &&
          deviceWithNoPhoneList.length
        "
        class="text-red-600 text-xs pl-6 pb-4"
      >
        <div class="font-semibold">
          The following selected Company Devices will not be sent this SMS
          because it does not have a valid phone number:
        </div>
        <ul class="disc-list">
          <li
            v-for="(device, index) in deviceWithNoPhoneList"
            :key="`device-phone-${index}`"
          >
            {{ device }}
          </li>
        </ul>
      </div>
      <div
        v-if="
          selectedChannel === 'Device' &&
          deviceWithNonActiveStatusList.length
        "
        class="text-red-600 text-xs pl-6 pb-4"
      >
        <div class="font-semibold">
          The following selected Company Devices will not be sent this SMS
          because it is not Active:
        </div>
        <ul class="disc-list">
          <li
            v-for="(device, index) in deviceWithNonActiveStatusList"
            :key="`device-status-${index}`"
          >
            {{ device }}
          </li>
        </ul>
      </div>
      </template>
      
      <template v-if="showEmailCheckbox">
        <el-radio
          label="Email"
          :disabled="isEmailDisabled"
        >
          <p class="wrap-text"><b>Email</b> {{ emailLabel }}</p>
        </el-radio>
        <div
          v-if="
            (selectedChannel === 'Email' || isEmailDisabled) &&
            staffEmailOnlyList.length
          "
          class="text-red-600 text-xs pl-6 pb-4"
        >
          <div class="font-semibold">
            The following selected {{ $t("label.associates") }} will not be sent
            this email because they have opted out of emails:
          </div>
          <ul class="disc-list">
            <li v-for="(staff, index) in staffEmailOnlyList" :key="`email-${index}`">
              {{ staff }}
            </li>
          </ul>
        </div>
      </template>
      
      <el-radio
        label="InApp"
        :disabled="!showInAppCheckbox"
      >
        <p class="wrap-text"><b>In-app message</b> using the Hera App</p>
      </el-radio>
    </el-radio-group>
  </div>
</template>

<script>
import { MESSAGE_LOCATIONS } from '@/views/Settings/Messenger/constants/messageLocations.js'
import { getFullName } from '@/utilities/associate'

export default {
  name: "MessageDeliveryChannelOptions",
  props: {
    dailyRosterTableData: {
      type: Array,
      default: () => [],
    },
    staffData: {
      type: Object,
      default: null,
    },
    isRosterDateToday: {
      type: Boolean,
      default: false,
    },
    forSingleRoute: {
      type: Boolean,
      default: false,
    },
    showDeviceCheckbox: {
      type: Boolean,
      default: true,
    },
    isCustomMessageDrawer: {
      type: Boolean,
      default: false
    },
    messageLocation: {
      type: String,
      default: 'dailyRosterCustomMessages'
    }
  },
  data() {
    return {
      selectedChannel: 'SMS', // Default to SMS personal
    };
  },
  computed: {
    activeRoutes() {
      return this.getActiveRoutes();
    },
    staffEmailOptedList() {
      return this.activeRoutes
        .filter((route) => route.staff?.id && route.staff?.receiveEmailMessages !== 0 && route.staff?.receiveEmailMessages !== false)
        .map((route) => getFullName(route.staff));
    },
    staffSmsOptedList() {
      return this.activeRoutes
        .filter((route) => route.staff?.id && route.staff?.receiveTextMessages !== 0 && route.staff?.receiveTextMessages !== false)
        .map((route) => getFullName(route.staff));
    },
    deviceWithNoPhoneList() {
      return this.activeRoutes
        .filter((route) => route.device && route.device.id && !route.device.phoneNumber)
        .map((route) => `${route.device.deviceName}`)
        .filter((deviceName) => deviceName.trim() !== "");
    },
    deviceWithNonActiveStatusList() {
      return this.activeRoutes
        .filter((route) => route.device && route.device.id && route.device.status !== "Active")
        .map((route) => `${route.device.deviceName}`)
        .filter((deviceName) => deviceName.trim() !== "");
    },
    staffEmailOnlyList() {
      const getActiveStaff = (route) => {
        // If route is explicitly marked as not active and has a replacement, use the replacement
        if (route.isNotActive === true && route.replaceByRoute?.items?.length > 0) {
          // Recursively get the active staff from the replacement route
          return getActiveStaff(route.replaceByRoute.items[0]);
        }
        // Otherwise return this route's staff
        return route.staff;
      };

      return this.activeRoutes
        .map((route) => this.getActiveStaff(route))
        .filter((staff) => staff?.id && (staff?.receiveEmailMessages === 0 || staff?.receiveEmailMessages === false))
        .map((staff) => getFullName(staff));
    },
    staffSmsOnlyList() {
      const getActiveStaff = (route) => {
        // If route is explicitly marked as not active and has a replacement, use the replacement
        if (route.isNotActive === true && route.replaceByRoute?.items?.length > 0) {
          // Recursively get the active staff from the replacement route
          return getActiveStaff(route.replaceByRoute.items[0]);
        }
        // Otherwise return this route's staff
        return route.staff;
      };

      return this.activeRoutes
        .map((route) => this.getActiveStaff(route))
        .filter((staff) => staff?.id && (staff?.receiveTextMessages === 0 || staff?.receiveTextMessages === false))
        .map((staff) => getFullName(staff));
    },
    smsLabel() {
      if (this.forSingleRoute) {
        return `to ${this.$t("label.associate")}'s personal phone`;
      }
      const count = this.staffSmsOptedList.length;
      return `to ${this.$t("label.associate")}'s personal phone (${count} ${this.$t("label.associate")}${
        count === 1 ? "" : "s"
      })`;
    },
    emailLabel() {
      if (this.forSingleRoute) {
        return `to ${this.$t("label.associate")}'s email`;
      }
      const count = this.staffEmailOptedList.length;
      return `to ${this.$t("label.associate")}'s email (${count} ${this.$t("label.associate")}${
        count === 1 ? "" : "s"
      })`;
    },
    deviceLabel() {
      if (this.forSingleRoute) {
        return `to assigned company Device`;
      }
      const count = this.activeRoutes.filter(
        (route) => route.device && route.device.id
      ).length;
      return `to assigned company Device (${count} ${this.$t(
        "label.associate"
      )}${count === 1 ? "" : "s"})`;
    },
    hasSmsRecipients() {
      return this.activeRoutes.filter(route => route.staff?.id).length > 0;
    },
    hasEmailRecipients() {
      return this.activeRoutes.filter(route => route.staff?.id).length > 0;
    },
    hasDeviceRecipients() {
      return this.activeRoutes.filter(route => route.device && route.device.id).length > 0;
    },
    activeLocation() {
      return this.messageLocation;
    },
    locationConfig() {
      return MESSAGE_LOCATIONS.find(loc => loc.key === this.activeLocation) || {};
    },
    showInAppCheckbox() {
      return this.locationConfig.allowsInApp && !!this.$store.getters.isMessengerV2Enabled;
    },
    allowDeviceCheckbox() {
      return this.locationConfig.allowsDeviceSms;
    },
    showEmailCheckbox() {
      if (!this.locationConfig.allowsEmail) return false;
      if(this.isCustomMessageDrawer) {
        return this.$store.state?.userInfo?.tenant?.featureAccessCustomEmailSending && 
             this.$store.state?.userInfo?.tenant?.featureEnabledCustomEmailSending
      }
      return true;
    },
    isSmsDisabled() {
      return (this.activeRoutes.filter(route => route.staff?.id).length === 0 || this.staffSmsOnlyList.length === this.activeRoutes.length);
    },
    isEmailDisabled() {
      return !this.showEmailCheckbox || this.staffEmailOnlyList.length === this.activeRoutes.length;
    },
    isDeviceDisabled() {
      // Device is disabled if:
      // 1. No device recipients available
      // 2. MESSAGE_LOCATIONS doesn't allow device SMS for this location
      // 3. For daily roster messages, must be today's date
      const disabled = !this.hasDeviceRecipients || !this.allowDeviceCheckbox || 
        (this.activeLocation.startsWith('dailyRoster') && !this.isRosterDateToday);
      return disabled;
    },
  },
  watch: {
    forSingleRoute: {
      immediate: true,
      handler() {
        this.setDefaultChannel();
      }
    },
    isRosterDateToday() {
      this.setDefaultChannel();
    },
    activeRoutes: {
      handler() {
        this.setDefaultChannel();
      },
      deep: true,
    },
    messageLocation: {
      immediate: true,
      handler() {
        this.setDefaultChannel();
      }
    },
    selectedChannel: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('selectedChannel', newValue);
        }
      },
      immediate: true,
    },
    '$store.state.userInfo.tenant.defaultCommunicationChannels': {
      handler() {
        this.setDefaultChannel();
      },
      deep: true,
    },
  },
  mounted() {
    this.setDefaultChannel();
  },
  methods: {
    getActiveStaff(route) {
      // If route is explicitly marked as not active and has a replacement, use the replacement
      if (route.isNotActive === true && route.replaceByRoute?.items?.length > 0) {
        // Recursively get the active staff from the replacement route
        return this.getActiveStaff(route.replaceByRoute.items[0]);
      }
      // Otherwise return this route's staff
      return route.staff;
    },
    getDefaultChannelFromTenant() {
      const rawDefaults = this.$store.state?.userInfo?.tenant?.defaultCommunicationChannels;
      
      let tenantDefaults;
      try {
        tenantDefaults = typeof rawDefaults === 'string' ? JSON.parse(rawDefaults) : rawDefaults;
      } catch (e) {
        tenantDefaults = rawDefaults;
      }
      
      
      if (tenantDefaults && tenantDefaults[this.activeLocation]) {
        const defaultChannel = tenantDefaults[this.activeLocation];
        const result = defaultChannel === 'sms_personal' ? 'SMS' :
                      defaultChannel === 'sms_device' ? 'Device' :
                      defaultChannel === 'email' ? 'Email' :
                      defaultChannel === 'in_app' ? 'InApp' : 'SMS';
        return result;
      }
      console.log('[MessageDeliveryChannelOptions] No tenant default found, returning SMS');
      return 'SMS';
    },
    setDefaultChannel() {
      const defaultFromTenant = this.getDefaultChannelFromTenant();
      
      // Check if the default channel is available and enabled
      if (defaultFromTenant === 'Device' && this.hasDeviceRecipients && this.isRosterDateToday && this.allowDeviceCheckbox) {
        this.selectedChannel = 'Device';
      } else if (defaultFromTenant === 'SMS' && this.hasSmsRecipients && !this.isSmsDisabled) {
        this.selectedChannel = 'SMS';
      } else if (defaultFromTenant === 'Email' && this.showEmailCheckbox && !this.isEmailDisabled) {
        this.selectedChannel = 'Email';
      } else if (defaultFromTenant === 'InApp' && this.showInAppCheckbox) {
        this.selectedChannel = 'InApp';
      } else {
        // Fallback to first available option
        if (this.hasSmsRecipients && !this.isSmsDisabled) {
          this.selectedChannel = 'SMS';
        } else if (this.hasDeviceRecipients && this.isRosterDateToday && this.allowDeviceCheckbox) {
          this.selectedChannel = 'Device';
        } else if (this.showEmailCheckbox && !this.isEmailDisabled) {
          this.selectedChannel = 'Email';
        }
      }
    },
    getActiveRoutes() {
      // Handle single staff entity (for coaching, associate list, messenger)
      if (this.staffData) {
        return [{ staff: this.staffData }];
      }
      // Handle roster data (for daily roster)
      return this.dailyRosterTableData || [];
    }
  }
}
</script>

<style scoped>
.disc-list {
  list-style-type: disc;
  padding-left: 20px;
}

.disc-list li {
  display: list-item;
}

.wrap-text {
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
</style>

<style lang="scss">
.radio-group{
  .el-radio{
    display: block;
    width: 100%;
    padding-bottom: .75rem;
    &:last-child{
      padding-bottom: 0
    }
  }
  .el-radio__label{
    display: inline-flex !important;
  }
}

.radio-group-horizontal{
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  
  .el-radio{
    display: inline-block;
    width: auto;
    padding-bottom: 0;
    margin-right: 1rem;
    
    &:last-child{
      margin-right: 0;
    }
  }
  
  .el-radio__label{
    display: inline-flex !important;
  }
}
.el-radio.is-disabled.is-checked {
  .el-radio__input.is-disabled.is-checked .el-radio__inner {
    background-color: #0268BC;
    border-color: #0268BC;
    &::after {
      background-color: #FFFFFF;
    }
  }
  
  .el-radio__label {
    color: #0268BC;
    opacity: 1;
  }
}
</style>
